/**
 * Admin → Algemene mailboxen — API-laag. Alles onder /Api/algemene-mailboxen: op develop de engine-voordeur
 * (api/frontdoor/algemene_mailboxen.py), op main het Node-doorgeefluik (backend/routes/algemeneMailboxen.js).
 * Zelfde paden op beide branches; rechten: admin_settings (info@, PO, log), werving (ikwilwerken@).
 */
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';

export const BASIS = `${BACKEND_URL}/Api/algemene-mailboxen`;

export async function vraag(basis, pad, opties = {}) {
  const r = await authFetch(`${basis}${pad}`, {
    ...opties,
    headers: { 'Content-Type': 'application/json', ...(opties.headers || {}) },
    body: opties.body !== undefined ? JSON.stringify(opties.body) : undefined,
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) {
    const detail = typeof d.detail === 'string' ? d.detail : (d.error || d.detail?.message || '');
    const fout = new Error(detail || `Fout ${r.status}`);
    fout.status = r.status;
    throw fout;
  }
  return d;
}

const v = (pad, opties) => vraag(BASIS, pad, opties);

export const mailboxApi = {
  instellingen: () => v('/instellingen'),
  zetSchakelaar: (naam, niveau) => v(`/schakelaars/${encodeURIComponent(naam)}`, { method: 'PUT', body: { niveau } }),
  zetInfo: (body) => v('/info', { method: 'PUT', body }),
  zetPoOntvangers: (ontvangers, matchOntvangers) => v('/po/ontvangers', {
    method: 'PUT',
    body: matchOntvangers && matchOntvangers.length ? { ontvangers, match_ontvangers: matchOntvangers } : { ontvangers },
  }),
  poOverzicht: () => v('/po/overzicht'),
  poBijlage: (sleutel, nr) => v(`/po/bijlage/${encodeURIComponent(sleutel)}/${Number(nr)}`),
  zetInGripp: (body) => v('/po/zet-in-gripp', { method: 'POST', body }),
  kloptNiet: (body) => v('/po/klopt-niet', { method: 'POST', body }),
  trekIn: (id) => v(`/po/acties/${id}`, { method: 'DELETE' }),
  terugkijkStart: (maanden) => v(`/po/terugkijken?maanden=${maanden}`, { method: 'POST' }),
  terugkijkStatus: () => v('/po/terugkijken/status'),
  terugkijkLaatste: () => v('/po/terugkijken/laatste'),
  log: () => v('/log'),
  // "Verwerken zoals aangegeven" (link uit de info@-samenvatting): GET = alleen lezen, POST = na Bevestigen.
  verwerkenPagina: (token, groep) => v(`/info/verwerken/${encodeURIComponent(token)}/${encodeURIComponent(groep)}`),
  verwerk: (token, groep, verwacht) => v(`/info/verwerken/${encodeURIComponent(token)}/${encodeURIComponent(groep)}`, { method: 'POST', body: { verwacht } }),
  werving: () => v('/werving'),
  zetConcepten: (niveau) => v('/werving/concepten', { method: 'PUT', body: { niveau } }),
};

/** Werving-sjablonen + routering. `basis` is te overschrijven zodat de wervingsmodule hem kan hergebruiken. */
export function wervingApi(basis = `${BASIS}/werving`) {
  const w = (pad, opties) => vraag(basis, pad, opties);
  return {
    sjablonen: () => w('/sjablonen'),
    maak: (body) => w('/sjablonen', { method: 'POST', body }),
    wijzig: (id, body) => w(`/sjablonen/${id}`, { method: 'PATCH', body }),
    verwijder: (id) => w(`/sjablonen/${id}`, { method: 'DELETE' }),
    voorbeeld: (categorie) => w(`/sjablonen/voorbeeld/${encodeURIComponent(categorie)}`),
    routering: () => w('/routering'),
    zetRoute: (categorie, ontvangers) => w(`/routering/${encodeURIComponent(categorie)}`, { method: 'PUT', body: { ontvangers } }),
    verwijderRoute: (categorie) => w(`/routering/${encodeURIComponent(categorie)}`, { method: 'DELETE' }),
  };
}

export const NIVEAU_LABEL = { off: 'Uit', dry: 'Proef', on: 'Aan' };

/** Placeholders invullen zoals de engine dat doet (alleen voor de voorbeeldweergave tijdens bewerken). */
export const VOORBEELD_WAARDEN = {
  voornaam: 'Sam', vacature: 'Editor', afzender: 'Joanne',
  persoonlijk: 'Leuk om te lezen dat je al veel met After Effects werkt.',
};

export function vulVoorbeeld(tekst, waarden = VOORBEELD_WAARDEN) {
  return String(tekst || '').replace(/\{(\w+)\}/g, (m, k) => (k in waarden ? waarden[k] : m));
}

export function onbekendePlaceholders(tekst, toegestaan) {
  const set = new Set(toegestaan || []);
  return [...new Set([...String(tekst || '').matchAll(/\{([^{}]*)\}/g)].map((m) => m[1]))].filter((p) => !set.has(p));
}

export const adressen = (tekst) => String(tekst || '').split(/[\s,;]+/).map((a) => a.trim().toLowerCase()).filter(Boolean);
